import { Minus, Plus, Banknote, QrCode, CreditCard, Wallet, Utensils, ShoppingBasket, Ticket, Landmark, Handshake, BookOpen, MoreHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { MoneyInput } from "@/components/MoneyInput";
import { brl, PAY_LABELS } from "@/lib/api";
import { activeItems } from "@/lib/mobile";
import { cardCls, MTabs } from "@/components/mobile/parts";
import { cn } from "@/lib/utils";

const ICONS = { dinheiro: Banknote, pix: QrCode, credito: CreditCard, debito: Wallet, vale_refeicao: Utensils, vale_alimentacao: ShoppingBasket, voucher: Ticket, transferencia: Landmark, convenio: Handshake, fiado: BookOpen, outros: MoreHorizontal };
const SHORT = { credito: "Crédito", debito: "Débito", vale_refeicao: "V. Refeição", vale_alimentacao: "V. Alimentação" };
const r2 = (v) => Math.round(v * 100) / 100;
export const money = (v) => {
  const s = String(v ?? "");
  return r2(Number(s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s) || 0);
};
export const MODES = [["toda", "Conta toda"], ["dividir", "Dividir conta"], ["itens", "Itens selecionados"]];

export function payPlan({ c, mode, split, people, share, sel, recv, method }) {
  const items = activeItems(c).filter((i) => sel.includes(i.id));
  const raw = mode === "toda" ? c.remaining : mode === "itens" ? items.reduce((s, i) => s + i.total, 0) : split === "iguais" ? c.remaining / people : money(share);
  const due = r2(Math.min(c.remaining, raw));
  const got = money(recv);
  const pay = r2(Math.min(got, due));
  const partial = got > 0 && got < due - 0.001;
  const label = mode === "itens" ? "Itens selecionados" : mode === "dividir" ? (split === "iguais" ? `1/${people} da conta` : "Valor por pessoa") : "";
  return {
    items, due, got, pay, partial, label,
    change: method === "dinheiro" ? r2(Math.max(0, got - due)) : 0,
    over: method !== "dinheiro" && got > due + 0.001,
    closes: c.remaining <= 0 || (pay > 0 && pay >= c.remaining - 0.001),
    after: r2(Math.max(0, c.remaining - pay)),
    itemIds: mode === "itens" && !partial ? items.map((i) => i.id) : [],
  };
}

export const ModeBar = ({ mode, setMode }) => <MTabs value={mode} onChange={setMode} items={MODES} testId="m-pay-mode" />;

export function SplitBox({ split, setSplit, people, setPeople, share, setShare, due }) {
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-split-box">
      <MTabs value={split} onChange={setSplit} items={[["iguais", "Partes iguais"], ["livre", "Valor livre"]]} testId="m-split-mode" />
      {split === "iguais" ? (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Pessoas</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setPeople(Math.max(1, people - 1))} className="grid h-10 w-10 place-items-center rounded-full border border-slate-300" data-testid="m-split-dec"><Minus className="h-4 w-4" /></button>
            <span className="w-8 text-center font-mono text-xl font-bold" data-testid="m-split-people">{people}</span>
            <button onClick={() => setPeople(Math.min(50, people + 1))} className="grid h-10 w-10 place-items-center rounded-full bg-[#1565c0] text-white" data-testid="m-split-inc"><Plus className="h-4 w-4" /></button>
          </div>
        </div>
      ) : (
        <Input value={share} onChange={(e) => setShare(e.target.value)} inputMode="decimal" placeholder="Quanto esta pessoa vai pagar (R$)" className="h-12 bg-white text-lg" data-testid="m-split-share-input" />
      )}
      <p className="flex justify-between text-sm"><span className="text-slate-500">{split === "iguais" ? "Cada pessoa paga" : "Valor desta pessoa"}</span><b className="font-mono" data-testid="m-split-due">{brl(due)}</b></p>
    </div>
  );
}

export function ItemsPick({ c, sel, setSel }) {
  const list = activeItems(c);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <ul className={cn(cardCls, "divide-y divide-slate-100")} data-testid="m-items-pick">
      {list.map((i) => (
        <li key={i.id}><label className={cn("flex items-center gap-3 px-4 py-3 text-sm", i.paid && "opacity-50")}>
          <Checkbox checked={sel.includes(i.id)} disabled={i.paid} onCheckedChange={() => toggle(i.id)} data-testid={`m-pick-item-${i.id}`} />
          <span className="flex-1">{i.qty}x {i.name}{i.paid && <span className="ml-2 text-xs font-semibold text-emerald-600">pago</span>}</span>
          <span className="font-mono">{brl(i.total)}</span>
        </label></li>
      ))}
    </ul>
  );
}

export function MethodGrid({ methods, method, setMethod }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="m-pay-methods">
      {methods.map((m) => {
        const Icon = ICONS[m] || MoreHorizontal;
        return (
          <button key={m} onClick={() => { setMethod(m); if (m === "dinheiro") document.getElementById("m-pay-amount")?.focus(); }} data-testid={`m-pay-method-${m}`}
            className={cn("flex h-16 flex-col items-center justify-center gap-1 rounded-xl border text-xs font-semibold transition-colors", method === m ? "border-[#1565c0] bg-[#1565c0] text-white" : "border-slate-300 bg-white text-slate-700")}>
            <Icon className="h-5 w-5" />{SHORT[m] || PAY_LABELS[m]}
          </button>
        );
      })}
    </div>
  );
}

export function AmountBox({ recv, setRecv, plan, method, partialHint }) {
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-amount-box">
      <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Valor a cobrar</span><b className="font-mono text-lg" data-testid="m-pay-due">{brl(plan.due)}</b></div>
      <div className="flex gap-2">
        <MoneyInput id="m-pay-amount" value={recv} onChange={setRecv} placeholder={method === "dinheiro" ? "Valor recebido (R$) *" : "Valor pago (R$) *"} className="h-12 flex-1 bg-white font-mono text-lg" data-testid="m-pay-amount-input" />
        <button onClick={() => setRecv(plan.due.toFixed(2).replace(".", ","))} disabled={!plan.due} className="rounded-xl border border-slate-300 px-3 text-xs font-semibold text-[#1565c0]" data-testid="m-pay-exact-btn">Valor exato</button>
      </div>
      {plan.change > 0 && <p className="rounded-xl bg-emerald-100 p-3 text-center font-display text-2xl font-bold text-emerald-700" data-testid="m-pay-change">Troco: {brl(plan.change)}</p>}
      {plan.partial && <p className="rounded-xl bg-amber-100 p-3 text-sm text-amber-900" data-testid="m-pay-partial-hint">{partialHint || <>Pagamento parcial: a comanda continua aberta e fica faltando <b>{brl(plan.after)}</b>.</>}</p>}
      {plan.over && <p className="rounded-xl bg-red-100 p-3 text-sm text-red-800" data-testid="m-pay-over-hint">Em {PAY_LABELS[method]} o valor não pode passar de {brl(plan.due)}. Troco só no dinheiro.</p>}
    </div>
  );
}
